import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, GraduationCap, Play, CheckCircle2, MapPin, CalendarDays, BookOpen, Award } from "lucide-react";
import { useStudentCourses } from "@/hooks/useStudentCourses";
import { getStatusMeta } from "@/utils/enrolmentStatus";

const STATUS_CFG = {
  "In Progress": { cls: "bg-amber-500", label: "In Progress", icon: Play },
  Completed: { cls: "bg-emerald-500", label: "Completed", icon: CheckCircle2 },
};

const DEFAULT_COVER = {
  accent: "from-amber-400 via-orange-500 to-rose-500",
  icon: BookOpen,
};

export default function MyCourses() {
  const { courses, summary, loading, error } = useStudentCourses();

  const courseCards = useMemo(() => {
    return courses.map((course) => {
      const statusKey = course.isCompleted ? "Completed" : course.status || "In Progress";
      const status = STATUS_CFG[statusKey] || STATUS_CFG["In Progress"];
      const enrolmentMeta = getStatusMeta(course.workFlowStatusNo);
      const accent = course.image ? "from-sky-400 via-blue-500 to-indigo-600" : DEFAULT_COVER.accent;
      const Icon = course.image ? Award : DEFAULT_COVER.icon;

      return {
        ...course,
        statusKey,
        status,
        enrolmentMeta,
        accent,
        Icon,
      };
    });
  }, [courses]);

  return (
    <div className="space-y-6" data-testid="my-courses">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-[#0A2540]">My courses</h1>
          <p className="text-sm text-slate-500 mt-1">Courses you're enrolled in. Click a course to start or resume its enrolment.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="wc-badge bg-slate-100 text-slate-700 border border-slate-200">{summary.totalCourses} total</span>
          <span className="wc-badge bg-amber-50 text-amber-700 border border-amber-200">{summary.inProgressCourses} in progress</span>
          <span className="wc-badge bg-emerald-50 text-emerald-700 border border-emerald-200">{summary.completedCourses} completed</span>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading your courses…</div>
      ) : error ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">{error}</div>
      ) : courseCards.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
          You do not have any courses yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-5">
          {courseCards.map((course) => {
            const StatusIcon = course.status.icon;
            return (
              <div key={course.id} className="group rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-xl hover:shadow-slate-200/60 hover:-translate-y-1 transition-all duration-300" data-testid={`course-${course.id}`}>
                <div className="relative h-40 overflow-hidden">
                  <img src={course.image || "https://images.pexels.com/photos/887827/pexels-photo-887827.jpeg"} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${course.accent} mix-blend-multiply opacity-80`} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

                  <div className="absolute top-3 left-3 right-3 flex items-start justify-between">
                    <span className="wc-badge bg-white/95 text-[#0A2540] backdrop-blur border-0 shadow-sm">
                      <course.Icon className="w-3 h-3" /> {course.code}
                    </span>
                    <span className={`wc-badge border shadow-sm ${course.enrolmentMeta.className}`}>
                      <StatusIcon className="w-3 h-3" /> {course.enrolmentMeta.label}
                    </span>
                  </div>

                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <div className="text-[10px] uppercase font-bold tracking-widest opacity-90">{course.location || "TBC"}</div>
                    <div className="font-display font-extrabold text-xl leading-tight drop-shadow-md mt-0.5">{course.title}</div>
                  </div>
                </div>

                <div className="p-5">
                  <p className="text-sm text-slate-600 leading-snug">{course.description}</p>

                  <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {course.date || "TBC"}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {course.location || "TBC"}</span>
                  </div>

                  <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-slate-500 font-semibold uppercase tracking-wider">Your progress</span>
                      <span className={`font-bold ${course.progress >= 100 ? "text-emerald-600" : "text-orange-600"}`}>{course.progress}%</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${course.progress >= 100 ? "bg-gradient-to-r from-emerald-400 to-emerald-600" : "bg-gradient-to-r from-orange-400 to-orange-600"}`} style={{ width: `${Math.max(0, Math.min(100, course.progress))}%` }} />
                    </div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Next step</div>
                      <div className="text-sm font-semibold text-[#0A2540] truncate">{course.nextStep || "Continue your course"}</div>
                    </div>
                    <Link to="/student/enrol-new" state={{ studentBookingId: course.studentBookingId ?? course.id }} className="shrink-0 bg-[#0A2540] hover:bg-orange-500 text-white font-semibold text-xs px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5 transition-colors group-hover:bg-orange-500" data-testid={`enter-${course.id}`}>
                      {course.buttonText || "Enter"} <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-200 pt-4">
        <div className="text-xs text-slate-500">Looking for more? Browse our full course catalogue.</div>
        <Link to="/calendar" className="wc-btn-outline text-sm" data-testid="all-courses"><GraduationCap className="w-4 h-4" /> All courses</Link>
      </div>
    </div>
  );
}
